// ProjectDeleteModal — the module's ONE delete dialog, shared by the listing
// (row kebab + selection bar, Archived tab only) and the detail page's Danger
// Zone, so both ask the same way and can't drift apart.
//
// Type-to-confirm: the confirm button unlocks only once the project's own name
// is typed back verbatim (trimmed, case-sensitive — the GitHub convention).
//
// The dialog owns the DELETE call itself rather than taking a handler: every
// caller wants the same mutation, the same failure toast and the same "stays
// open on failure so it can be retried" behaviour. Callers get `onDeleted` for
// what only THEY know — the listing prunes its selection, the detail page
// navigates away from a project that no longer exists.

import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ConfirmModal, Input } from '@/components/common';
import { useDeleteProjectMutation } from '@/api/endpoints/projectsApi';

interface ProjectDeleteModalProps {
  /** The project being deleted; `null` keeps the dialog closed. Its name is
   *  both the copy's subject and the type-to-confirm gate. */
  project: { id: string; name: string } | null;
  onClose: () => void;
  /** Ran after a successful delete, before the dialog closes itself. */
  onDeleted?: (project: { id: string; name: string }) => void;
}

const ProjectDeleteModal = ({ project, onClose, onDeleted }: ProjectDeleteModalProps) => {
  const { t } = useTranslation();
  const [deleteProject, { isLoading: deleting }] = useDeleteProjectMutation();
  const [text, setText] = useState('');

  const name = project?.name ?? '';

  // Reset on every open, and on a switch straight from one project to another,
  // so a previous attempt can never leave the confirm button pre-enabled. Keyed
  // on the id, not the name — two projects may share a name.
  useEffect(() => setText(''), [project?.id]);

  const remove = async () => {
    if (!project) return;
    try {
      await deleteProject(project.id).unwrap();
      toast.success(t('projects_deleted', { name: project.name }));
      onDeleted?.(project);
      onClose();
    } catch {
      // Left open so the user can retry; the toast says why.
      toast.error(t('error_generic'));
    }
  };

  return (
    <ConfirmModal
      open={!!project}
      onClose={onClose}
      onConfirm={() => void remove()}
      tone='danger'
      icon='trash-2'
      title={t('projects_delete_modal_title')}
      description={
        <Trans i18nKey='projects_delete_confirm' values={{ name }} components={{ b: <strong /> }} />
      }
      confirmLabel={t('projects_delete_modal_title')}
      confirmVariant='danger'
      confirmDisabled={text.trim() !== name}
      loading={deleting}
    >
      {/* The project's own name is data, not copy — it is the placeholder
          verbatim, so no i18n key. */}
      <div className='project-delete-confirm'>
        <Input
          value={text}
          placeholder={name}
          aria-label={t('projects_field_name')}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
    </ConfirmModal>
  );
};

export default ProjectDeleteModal;
